import {normalizeLinkedInMemberUrn} from '../utils/normalizeLinkedInMemberUrn';

/**
 * Identités affichées quand l'extension refuse de fonctionner pour cause de
 * « conflit de compte » (`ACCOUNT_MISMATCH`).
 *
 * Pourquoi ce module : la décision de refus se prend sur l'`entityUrn`
 * (cf. `CsrfMessage`, `LoginMessage`, `ChangePasswordMessage`), alors que les
 * écrans montraient un `vanityName`. Quand seul l'`entityUrn` diffère — le cas
 * le plus fréquent — les deux profils affichés étaient identiques et le message
 * revenait à dire à l'utilisateur que son compte était en conflit avec
 * lui-même.
 *
 * Le module est volontairement PUR (aucun accès au DOM, aucune API extension) :
 * il est partagé par les trois surfaces d'affichage de l'extension (sharebox,
 * feed, popup) et testable sans navigateur. Le rendu vit dans `./view`.
 */

/**
 * `https://www.linkedin.com/in/<entityUrn>` RÉSOUT et redirige vers le profil
 * correspondant, exactement comme un `vanityName`. C'est ce qui permet de
 * rendre l'écart constatable : l'utilisateur clique et voit le profil
 * réellement enregistré, même si nous n'en connaissons pas le `vanityName` à
 * jour.
 */
const LINKEDIN_PROFILE_URL = 'https://www.linkedin.com/in/';

/**
 * Une chaîne vide n'est pas une valeur : c'est elle qui produisait « associé au
 * profil . » quand la session de l'extension ne portait pas le `vanityName`.
 */
const clean = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
};

/**
 * Identifiant nu du membre, sans le préfixe posé par LinkedIn (`fs_miniProfile`,
 * `fsd_profile` pour un compte migré…) : la même règle que celle sur laquelle les
 * handlers décident du conflit, pour que l'écran montre ce qui a été comparé.
 */
export const normalizeEntityUrn = (entityUrn: string | undefined): string | undefined =>
  clean(normalizeLinkedInMemberUrn(clean(entityUrn)));

export interface AccountMismatchInput {
  /** `entityUrn` enregistré côté PerfectPost : le champ qui a servi à la décision. */
  registeredEntityUrn?: string;
  /** `vanityName` enregistré côté PerfectPost, potentiellement périmé. */
  registeredVanityName?: string;
  /** `entityUrn` extrait en direct de la session LinkedIn ouverte. */
  currentEntityUrn?: string;
  /** `publicIdentifier` extrait en direct de la session LinkedIn ouverte. */
  currentVanityName?: string;
}

export interface ProfileIdentity {
  /** Identifiant retenu pour construire l'URL. `undefined` = aucune valeur connue. */
  identifier?: string;
  /** URL du profil, `undefined` quand aucun identifiant n'est connu. */
  url?: string;
  /** Valeur brute, telle quelle, pour recopie dans un message au support. */
  entityUrn?: string;
  /** Valeur brute, telle quelle, pour recopie dans un message au support. */
  vanityName?: string;
}

export interface AccountMismatchIdentities {
  /** Le profil associé au compte PerfectPost — identifié par son `entityUrn`. */
  registered: ProfileIdentity;
  /** Le compte LinkedIn ouvert dans le navigateur — identifié par son `publicIdentifier`. */
  current: ProfileIdentity;
}

const identity = (identifier: string | undefined, entityUrn?: string, vanityName?: string): ProfileIdentity => ({
  identifier,
  url: identifier === undefined ? undefined : `${LINKEDIN_PROFILE_URL}${identifier}`,
  entityUrn,
  vanityName,
});

/**
 * Construit les deux identités à afficher.
 *
 * Côté PerfectPost, l'`entityUrn` prime : c'est le champ comparé, donc le seul
 * qui distingue réellement les deux profils. Le `vanityName` ne sert que de
 * repli quand l'`entityUrn` est absent (association faite avant son
 * enregistrement).
 *
 * Côté navigateur, le `publicIdentifier` extrait en direct est juste et
 * lisible : il prime, l'`entityUrn` sert de repli.
 */
export function buildAccountMismatchIdentities(input: AccountMismatchInput): AccountMismatchIdentities {
  const registeredEntityUrn = normalizeEntityUrn(input.registeredEntityUrn);
  const registeredVanityName = clean(input.registeredVanityName);
  const currentEntityUrn = normalizeEntityUrn(input.currentEntityUrn);
  const currentVanityName = clean(input.currentVanityName);
  return {
    registered: identity(registeredEntityUrn ?? registeredVanityName, registeredEntityUrn, registeredVanityName),
    current: identity(currentVanityName ?? currentEntityUrn, currentEntityUrn, currentVanityName),
  };
}
